"use client";

import { cn } from "@/lib/utils";
import type { PadelTemplateData } from "../padel-template";
import { ScorecardShell, type ScorecardVariant } from "./scorecard-shell";

type ScorecardParticipant = {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  username: string | null;
};

type PadelScorecardProps = {
  data: PadelTemplateData | null;
  participants: ScorecardParticipant[];
  variant?: ScorecardVariant;
};

export function PadelScorecard({
  data,
  participants,
  variant = "feed",
}: PadelScorecardProps) {
  if (!data) return null;

  const showScore = data.hasScore !== false;
  if (!showScore) return null;

  const yourTeam = participants.filter(
    (p) => data.positions?.[p.id] === "your-team",
  );
  const opponentTeam = participants.filter(
    (p) => data.positions?.[p.id] === "opponent-team",
  );

  const yourWon = data.matchScore.yourSets > data.matchScore.opponentSets;
  const opponentWon = data.matchScore.opponentSets > data.matchScore.yourSets;

  return (
    <ScorecardShell variant={variant}>
      <div className="flex items-center justify-between">
        <div className="flex flex-col items-start">
          <span className="text-sm font-semibold text-text-primary">
            {yourTeam.map((p) => p.displayName.split(" ")[0]).join(" & ") ||
              "Team 1"}
          </span>
          {yourWon && (
            <span className="text-xs font-medium text-brand">Winner</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "text-2xl font-bold",
              yourWon ? "text-brand" : "text-text-primary",
            )}
          >
            {data.matchScore.yourSets}
          </span>
          <span className="text-text-secondary">–</span>
          <span
            className={cn(
              "text-2xl font-bold",
              opponentWon ? "text-error" : "text-text-primary",
            )}
          >
            {data.matchScore.opponentSets}
          </span>
        </div>
        <div className="flex flex-col items-end">
          <span className="text-sm font-semibold text-text-primary">
            {opponentTeam.map((p) => p.displayName.split(" ")[0]).join(" & ") ||
              "Team 2"}
          </span>
          {opponentWon && (
            <span className="text-xs font-medium text-error">Winner</span>
          )}
        </div>
      </div>

      {data.sets && data.sets.length > 0 && (
        <div className="mt-2 flex flex-wrap justify-center gap-2 border-t border-border pt-2">
          {data.sets.map((set, i) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: set scores are positional and never reorder
              key={`set-${i}-${set.yourScore}-${set.opponentScore}`}
              className="rounded bg-surface-alt px-2 py-0.5 text-xs font-medium text-text-secondary"
            >
              {set.yourScore}-{set.opponentScore}
            </span>
          ))}
        </div>
      )}
    </ScorecardShell>
  );
}
